// src/api/notes.ts
const API_BASE: string = (process.env.REACT_APP_API_BASE || 'http://localhost:5000').replace(/\/$/, '');

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

async function responseError(res: Response, fallback: string): Promise<Error> {
    const err = await res.json().catch(() => ({}));
    return new Error(err.message || `${fallback}: ${res.status}`);
}

// ── Types ──────────────────────────────────────────

export type FolderInfo = {
    id: string;
    name: string;
    color: string;
};

export type Folder = FolderInfo & {
    parent: string | null;
    noteCount: number;
    childFolderCount?: number;
    isPinned?: boolean;
    isTrashed?: boolean;
    deletedAt?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type Note = {
    id: string;
    title: string;
    content: string;
    type: 'text' | 'code' | 'list' | 'rich';
    color?: string;
    tags: string[];
    folder: FolderInfo | null;
    isPinned: boolean;
    isTrashed: boolean;
    deletedAt?: string | null;
    lastAutosavedAt?: string | null;
    versionCount?: number;
    isOwner?: boolean;
    canEdit?: boolean;
    owner?: {
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
    } | null;
    sharedWith?: Array<{
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        permission: 'view' | 'edit';
        sharedAt?: string;
    }>;
    sharePermission?: 'view' | 'edit' | null;
    wordCount: number;
    createdAt: string;
    updatedAt: string;
};

export type NoteVersion = {
    id: string;
    title: string;
    type: Note['type'];
    tags: string[];
    savedAt: string;
    reason: string;
};

export type VaultPreferences = {
    activeFilter: string;
    activeType: 'all' | Note['type'];
    contentView: 'both' | 'notes' | 'folders';
    viewMode: 'grid-large' | 'grid-small' | 'list' | 'simple';
    sortMode: 'created' | 'updated' | 'title' | 'type';
};

export async function fetchVaultPreferences(): Promise<VaultPreferences> {
    const res = await fetch(`${API_BASE}/api/notes/preferences`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch Vault preferences failed: ${res.status}`);
    const data = await res.json();
    return data.preferences;
}

export async function saveVaultPreferences(preferences: Partial<VaultPreferences>): Promise<VaultPreferences> {
    const res = await fetch(`${API_BASE}/api/notes/preferences`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(preferences),
    });
    if (!res.ok) throw new Error(`Save Vault preferences failed: ${res.status}`);
    const data = await res.json();
    return data.preferences;
}

// ── Folders ────────────────────────────────────────

export async function fetchFolders(options: { trashed?: boolean } = {}): Promise<{ folders: Folder[]; unfiledCount: number }> {
    const url = new URL(`${API_BASE}/api/notes/folders`);
    if (options.trashed) url.searchParams.set('trashed', 'true');
    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch folders failed: ${res.status}`);
    const data = await res.json();
    return { folders: data.folders, unfiledCount: data.unfiledCount };
}

export async function createFolder(name: string, color?: string, parent?: string | null): Promise<Folder> {
    const res = await fetch(`${API_BASE}/api/notes/folders`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name, color, parent }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Create folder failed: ${res.status}`);
    }
    const data = await res.json();
    return data.folder;
}

export async function updateFolder(id: string, updates: { name?: string; color?: string; parent?: string | null; isPinned?: boolean }): Promise<Folder> {
    const res = await fetch(`${API_BASE}/api/notes/folders/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(updates),
    });
    if (!res.ok) throw await responseError(res, 'Update folder failed');
    const data = await res.json();
    return data.folder;
}

export async function deleteFolder(id: string): Promise<void> {
    const url = new URL(`${API_BASE}/api/notes/folders/${encodeURIComponent(id)}`);
    const res = await fetch(url.toString(), {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw await responseError(res, 'Delete folder failed');
}

export async function deleteFolderPermanently(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/notes/folders/${encodeURIComponent(id)}/permanent`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw await responseError(res, 'Permanent folder delete failed');
}

export async function restoreFolder(id: string): Promise<Folder> {
    const res = await fetch(`${API_BASE}/api/notes/folders/${encodeURIComponent(id)}/restore`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw await responseError(res, 'Restore folder failed');
    const data = await res.json();
    return data.folder;
}

// ── Notes ──────────────────────────────────────────

export type BulkVaultIds = {
    noteIds?: string[];
    folderIds?: string[];
};

async function bulkRequest<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body: unknown, fallback: string): Promise<T> {
    const res = await fetch(`${API_BASE}/api/notes/bulk/${path}`, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await responseError(res, fallback);
    return res.json();
}

export async function bulkMoveVaultItems(input: BulkVaultIds & { folder: string | null }): Promise<{ movedNotes: number; movedFolders: number }> {
    return bulkRequest('move', 'PATCH', input, 'Move selected items failed');
}

export async function bulkCopyVaultItems(input: BulkVaultIds): Promise<{ copiedNotes: number; copiedFolders: number }> {
    return bulkRequest('copy', 'POST', input, 'Copy selected items failed');
}

export async function bulkTrashVaultItems(input: BulkVaultIds): Promise<{ trashedNotes: number; trashedFolders: number }> {
    return bulkRequest('trash', 'PATCH', input, 'Recycle selected items failed');
}

export async function bulkRestoreVaultItems(input: BulkVaultIds): Promise<{ restoredNotes: number; restoredFolders: number }> {
    return bulkRequest('restore', 'PATCH', input, 'Restore selected items failed');
}

export async function bulkDeleteVaultItems(input: BulkVaultIds & { emptyTrash?: boolean }): Promise<{ deletedNotes: number; deletedFolders: number }> {
    return bulkRequest('permanent', 'DELETE', input, 'Delete selected items failed');
}

export async function bulkPinVaultItems(input: BulkVaultIds & { isPinned?: boolean }): Promise<{ pinnedNotes: number; pinnedFolders: number; isPinned: boolean }> {
    return bulkRequest('pin', 'PATCH', input, 'Pin selected items failed');
}

export async function bulkTagNotes(noteIds: string[], tags: string[]): Promise<Note[]> {
    const data = await bulkRequest<{ notes: Note[] }>('tag', 'PATCH', { noteIds, tags }, 'Tag selected notes failed');
    return data.notes;
}

export async function bulkShareNotes(noteIds: string[], username: string, permission: 'view' | 'edit' = 'view'): Promise<Note[]> {
    const data = await bulkRequest<{ notes: Note[] }>('share', 'PATCH', { noteIds, username, permission }, 'Share selected notes failed');
    return data.notes;
}

export type FetchNotesParams = {
    folder?: string | null;
    pinned?: boolean;
    trashed?: boolean;
    search?: string;
    type?: string;
    shared?: boolean;
    sharedScope?: 'with' | 'by' | 'all';
    sort?: 'created' | 'updated' | 'title' | 'type';
    page?: number;
    limit?: number;
};

export type FetchNotesResult = {
    notes: Note[];
    total: number;
    page: number;
    hasMore: boolean;
};

export async function fetchNotes(params: FetchNotesParams = {}): Promise<FetchNotesResult> {
    const url = new URL(`${API_BASE}/api/notes`);
    if (params.folder) url.searchParams.set('folder', params.folder);
    if (params.pinned) url.searchParams.set('pinned', 'true');
    if (params.trashed) url.searchParams.set('trashed', 'true');
    if (params.shared) url.searchParams.set('shared', 'true');
    if (params.sharedScope) url.searchParams.set('sharedScope', params.sharedScope);
    if (params.search) url.searchParams.set('search', params.search);
    if (params.type) url.searchParams.set('type', params.type);
    if (params.sort) url.searchParams.set('sort', params.sort);
    if (params.page) url.searchParams.set('page', String(params.page));
    if (params.limit) url.searchParams.set('limit', String(params.limit));

    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch notes failed: ${res.status}`);
    return res.json();
}

export async function fetchNote(id: string): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${encodeURIComponent(id)}`, { headers: authHeaders() });
    if (!res.ok) throw await responseError(res, 'Fetch note failed');
    const data = await res.json();
    return data.note;
}

export type CreateNoteInput = {
    title: string;
    content?: string;
    type?: 'text' | 'code' | 'list' | 'rich';
    color?: string;
    tags?: string[];
    folder?: string | null;
};

export async function createNote(input: CreateNoteInput): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Create note failed: ${res.status}`);
    }
    const data = await res.json();
    return data.note;
}

export async function updateNote(id: string, updates: Partial<CreateNoteInput>): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error(`Update note failed: ${res.status}`);
    const data = await res.json();
    return data.note;
}

export async function autosaveNote(id: string, updates: Partial<CreateNoteInput>): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/autosave`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error(`Autosave note failed: ${res.status}`);
    const data = await res.json();
    return data.note;
}

export async function fetchNoteVersions(id: string): Promise<NoteVersion[]> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/versions`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch versions failed: ${res.status}`);
    const data = await res.json();
    return data.versions || [];
}

export async function restoreNoteVersion(id: string, versionId: string): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/versions/${versionId}/restore`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Restore version failed: ${res.status}`);
    const data = await res.json();
    return data.note;
}

export async function togglePin(id: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/pin`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Toggle pin failed: ${res.status}`);
    const data = await res.json();
    return data.isPinned;
}

export async function toggleTrash(id: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/trash`, {
        method: 'PATCH',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Toggle trash failed: ${res.status}`);
    const data = await res.json();
    return data.isTrashed;
}

export async function deleteNotePermanently(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/notes/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Delete note failed: ${res.status}`);
}

export async function shareNote(id: string, username: string, permission: 'view' | 'edit'): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/share`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ username, permission }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Share note failed: ${res.status}`);
    }
    const data = await res.json();
    return data.note;
}

export async function unshareNote(id: string, userId: string): Promise<Note> {
    const res = await fetch(`${API_BASE}/api/notes/${id}/share/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Remove access failed: ${res.status}`);
    }
    const data = await res.json();
    return data.note;
}
